// Marco de todas las pantallas de la tienda, con sesión o sin cuenta (decisión D49). Arriba, la cabecera con el logo y
// el nombre de la empresa y el botón de modo claro u oscuro; desde tablet suma los accesos (Lo de siempre, Mis pedidos,
// Carrito, Cuenta). En el celular, la barra de abajo (Inicio, Buscar, Pedidos, Carrito, Entrar o Cuenta) y, si el
// carrito tiene algo, una barra flotante para verlo. Sin cuenta, lo que pide sesión lleva a entrar y vuelve. En modo
// vendedor (decisión D60), "Visitas" en lugar de "Inicio" y, abajo de la cabecera, para qué cliente se está pidiendo.
import { formatoGs } from '../compartido';
import { cx, Icono } from '@reparto/ui';
import { type ReactNode, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { resumen } from '../datos/carrito';
import { cargarResumen, useChat } from '../datos/chat';
import { useEnLinea } from '../datos/consultas';
import { useNegocio } from '../datos/cuenta';
import { useCarrito } from '../datos/estado-carrito';
import { plural } from '../datos/formato';
import { useMarca } from '../datos/marca';
import { useSesion, useVendedor } from '../datos/sesion';
import { alternarTema, useTema } from '../datos/tema';
import { usePendientes } from '../datos/vendedor';
import { Logo } from './Bienvenida';

/** Donde ya se ve el carrito (o no tiene sentido), la barra flotante no aparece. */
const SIN_BARRA = ['/carrito', '/entrar', '/registro', '/confirmado', '/negocio', '/mensajes'];

export const barraCarritoVisible = (ruta: string, articulos: number) =>
  articulos > 0 && !SIN_BARRA.some((r) => ruta === r || ruta.startsWith(`${r}/`));

export function Pantalla({
  titulo,
  subtitulo,
  volver,
  pie,
  pieSoloCelular,
  sinNav,
  amplia,
  children,
}: {
  titulo?: string;
  subtitulo?: string;
  /** A dónde lleva "‹ Volver" (sin él, no hay). */
  volver?: string | (() => void);
  /** Queda pegado abajo, arriba de la navegación (el total y "Confirmar pedido"). */
  pie?: ReactNode;
  /** El pie desaparece en computadora: su contenido está en la columna del costado. */
  pieSoloCelular?: boolean;
  sinNav?: boolean;
  /** Más ancho en computadora (grillas de productos, carrito en dos columnas). */
  amplia?: boolean;
  children: ReactNode;
  /** Ya no se usan (la cabecera es una sola para todas las pantallas); se aceptan por compatibilidad. */
  cabecera?: ReactNode;
  buscador?: ReactNode;
}) {
  const navegar = useNavigate();
  const { pathname } = useLocation();
  const refPie = useRef<HTMLDivElement>(null);
  const articulos = useCarrito((s) => s.carrito.lineas.length);
  const conBarra = !pie && barraCarritoVisible(pathname, articulos);
  const hayPie = !!pie;
  // El alto del pie queda en --alto-pie: los avisos flotantes salen arriba de él (estilos.css).
  useEffect(() => {
    const el = refPie.current;
    const raiz = document.documentElement.style;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const o = new ResizeObserver(() => raiz.setProperty('--alto-pie', `${el.offsetHeight}px`));
    o.observe(el);
    return () => {
      o.disconnect();
      raiz.setProperty('--alto-pie', '0px');
    };
  }, [hayPie]);
  const ancho = amplia ? 'max-w-6xl' : 'max-w-2xl';
  return (
    // overflow-x-clip: si algo se pasara de ancho, se corta acá y no ensancha la página (en el celular eso corre la
    // barra de abajo y la del carrito). clip, no hidden: no crea otra zona de scroll y la cabecera sigue pegada arriba.
    <div className="flex min-h-dvh flex-col overflow-x-clip bg-fondo">
      <CabeceraSitio />
      <SinSenal />
      <BandaVendedor />
      <main className={cx('mx-auto w-full flex-1', ancho)}>
        {titulo ? (
          <div className="px-4 pt-4">
            {volver ? (
              <button
                type="button"
                onClick={() => (typeof volver === 'function' ? volver() : navegar(volver))}
                className="-ml-2 inline-flex min-h-11 items-center gap-1 rounded-full px-2 text-[15px] font-medium text-stone-700 hover:bg-stone-200/60 focus-visible:outline-2 focus-visible:outline-primary-600"
              >
                <Icono nombre="chevron_left" tamano={22} />
                Volver
              </button>
            ) : null}
            <h1 className="mt-1 text-[28px] leading-tight font-bold text-stone-900 md:text-[32px]">{titulo}</h1>
            {subtitulo ? <p className="mt-0.5 text-[15px] text-stone-600">{subtitulo}</p> : null}
          </div>
        ) : null}
        {children}
        {conBarra ? <div aria-hidden="true" className="h-24 md:hidden" /> : null}
      </main>
      {pie ? (
        <div
          ref={refPie}
          className={cx(
            'sticky z-20 mx-auto w-full px-3 pt-2 pb-3',
            ancho,
            sinNav ? 'seguro-abajo bottom-0' : 'bottom-(--alto-nav) md:pb-[max(0.75rem,env(safe-area-inset-bottom))]',
            pieSoloCelular && 'lg:hidden',
          )}
        >
          {pie}
        </div>
      ) : null}
      {conBarra ? <BarraCarrito /> : null}
      {sinNav ? null : (
        <>
          <div aria-hidden="true" className="h-(--alto-nav) shrink-0 md:hidden" />
          <NavInferior />
        </>
      )}
    </div>
  );
}

function SinSenal() {
  const enLinea = useEnLinea();
  const vendedor = useVendedor();
  if (enLinea) return null;
  return (
    <div role="status" className="flex items-center justify-center gap-2 bg-stone-800 px-4 py-2 text-[15px] text-white">
      <Icono nombre="signal_disconnected" tamano={20} />
      {vendedor
        ? 'Sin señal. Lo que anotes queda guardado y se manda solo cuando vuelva.'
        : 'Sin señal. Tu pedido queda guardado en el celular.'}
    </div>
  );
}

/**
 * Modo vendedor (decisión D60): para qué cliente se está pidiendo (y volver a su visita) y cuántas cosas quedaron sin
 * mandar. En "Mis visitas" no hace falta: ahí se ve todo.
 */
function BandaVendedor() {
  const vendedor = useVendedor();
  const negocio = useNegocio();
  const sinMandar = usePendientes((s) => s.lista.length);
  const { pathname } = useLocation();
  if (!vendedor || pathname.startsWith('/visitas')) return null;
  if (!negocio && !sinMandar) return null;
  return (
    <div className="border-b border-primary-200 bg-primary-50 text-primary-950" data-banda-vendedor="">
      <div className="mx-auto flex w-full max-w-6xl items-center gap-2 px-4 py-1.5 text-[15px]">
        <Icono nombre="badge" tamano={20} className="shrink-0 text-primary-700" />
        <p className="min-w-0 flex-1 truncate">
          {negocio ? (
            <>
              Pidiendo para <b className="font-semibold">{negocio.nombre}</b>
            </>
          ) : (
            'Modo vendedor'
          )}
          {sinMandar ? (
            <span className="text-amber-800"> · {plural(sinMandar, 'sin mandar', 'sin mandar')}</span>
          ) : null}
        </p>
        <Link
          to={negocio ? `/visitas/${negocio.id}` : '/visitas'}
          className="inline-flex min-h-10 shrink-0 items-center gap-1 rounded-full px-3 font-semibold text-primary-800 hover:bg-primary-100"
        >
          {negocio ? 'Visita' : 'Mis visitas'}
          <Icono nombre="chevron_right" tamano={20} />
        </Link>
      </div>
    </div>
  );
}

/** Modo claro u oscuro (por defecto claro). */
export function BotonTema() {
  const oscuro = useTema((s) => s.tema === 'oscuro');
  return (
    <button
      type="button"
      onClick={alternarTema}
      aria-label={oscuro ? 'Pasar a modo claro' : 'Pasar a modo oscuro'}
      title={oscuro ? 'Modo claro' : 'Modo oscuro'}
      className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-stone-200 bg-white text-stone-700 hover:bg-stone-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
    >
      <Icono nombre={oscuro ? 'light_mode' : 'dark_mode'} tamano={22} />
    </button>
  );
}

/**
 * Mensajes con la distribuidora (decisión D53), en la cabecera con los sin leer. Pide el resumen al abrir cada pantalla
 * (como mucho uno por minuto; el socket lo mantiene al día). No aparece si la empresa no tiene el chat.
 */
function BotonMensajes() {
  const negocio = useNegocio();
  const habilitado = useChat((s) => s.habilitado);
  const noLeidos = useChat((s) => (s.negocioId === negocio?.id ? s.noLeidos : 0));
  const { pathname } = useLocation();
  useEffect(() => {
    if (negocio) void cargarResumen(negocio.id, { siVencido: true });
  }, [negocio?.id]);
  if (!negocio || !habilitado) return null;
  const activo = pathname === '/mensajes';
  return (
    <Link
      to="/mensajes"
      aria-label={noLeidos ? `Mensajes, ${noLeidos} sin leer` : 'Mensajes'}
      title="Mensajes"
      className={cx(
        'relative grid h-11 w-11 shrink-0 place-items-center rounded-full border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
        activo
          ? 'border-primary-200 bg-primary-50 text-primary-800'
          : 'border-stone-200 bg-white text-stone-700 hover:bg-stone-100',
      )}
    >
      <Icono nombre="chat" relleno={activo} tamano={22} />
      {noLeidos ? <Insignia n={noLeidos} className="-top-1.5 -right-1.5" /> : null}
    </Link>
  );
}

function Insignia({ n, className }: { n: number; className?: string }) {
  return (
    <span
      className={cx(
        'absolute grid h-5.5 min-w-5.5 place-items-center rounded-full bg-primary-600 px-1 text-xs font-bold text-sobre-marca tabular-nums ring-2 ring-white',
        className,
      )}
    >
      {n > 99 ? '99+' : n}
    </span>
  );
}

/** Logo y nombre de la empresa; desde tablet, los accesos. */
function CabeceraSitio() {
  const marca = useMarca((s) => s.marca);
  const conSesion = useSesion((s) => !!s.sesion);
  const vendedor = useVendedor();
  const carrito = useCarrito((s) => s.carrito);
  const { articulos, monto } = resumen(carrito);
  // Sin cuenta, lo que es de un cliente lleva a entrar (y vuelve ahí).
  const ir = (ruta: string) => (conSesion ? ruta : '/entrar');
  const enlace = ({ isActive }: { isActive: boolean }) =>
    cx(
      'relative hidden min-h-11 items-center gap-2 rounded-full px-3 text-[15px] font-semibold md:inline-flex',
      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600',
      isActive ? 'bg-primary-50 text-primary-800' : 'text-stone-800 hover:bg-stone-200/60',
    );
  return (
    <header className="seguro-arriba sticky top-0 z-40 border-b border-stone-200 bg-white/95 shadow-[0_1px_3px_rgb(0_0_0/0.06)] backdrop-blur">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-2">
        <Link
          to="/"
          className="flex min-h-12 min-w-0 items-center gap-2.5 rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
        >
          <Logo marca={marca} tamano={40} />
          <span className="truncate text-xl font-bold text-stone-900">{marca?.nombre ?? 'Tienda'}</span>
        </Link>
        <nav aria-label="Tu cuenta" className="flex shrink-0 items-center gap-1">
          {/* "Lo de siempre" es de un cliente; sin cuenta, el mismo lugar es para buscar en el catálogo. */}
          {vendedor ? (
            <NavLink to="/visitas" className={enlace}>
              <Icono nombre="route" tamano={22} />
              Mis visitas
            </NavLink>
          ) : conSesion ? (
            <NavLink to="/lo-de-siempre" className={enlace}>
              <Icono nombre="replay" tamano={22} />
              Lo de siempre
            </NavLink>
          ) : (
            <NavLink to="/buscar" className={enlace}>
              <Icono nombre="search" tamano={22} />
              Buscar
            </NavLink>
          )}
          <NavLink to={ir('/pedidos')} className={enlace}>
            <Icono nombre="receipt_long" tamano={22} />
            Mis pedidos
          </NavLink>
          <NavLink to="/carrito" className={enlace}>
            <span className="relative">
              <Icono nombre="shopping_cart" tamano={22} />
              {articulos ? <Insignia n={articulos} className="-top-2.5 -right-3" /> : null}
            </span>
            Carrito
            {articulos && monto ? (
              <span className="hidden text-primary-700 tabular-nums lg:inline">{formatoGs(monto)}</span>
            ) : null}
          </NavLink>
          <NavLink to={conSesion ? '/perfil' : '/entrar'} className={enlace}>
            <Icono nombre={conSesion ? 'person' : 'login'} tamano={22} />
            {conSesion ? 'Cuenta' : 'Entrar'}
          </NavLink>
          {conSesion && !vendedor ? <BotonMensajes /> : null}
          <BotonTema />
        </nav>
      </div>
    </header>
  );
}

function NavInferior() {
  const conSesion = useSesion((s) => !!s.sesion);
  const vendedor = useVendedor();
  const articulos = useCarrito((s) => s.carrito.lineas.length);
  const items: Array<{ a: string; texto: string; icono: string; fin: boolean; prestado?: boolean }> = [
    // El preventista arranca por sus visitas; el catálogo es el del cliente que eligió.
    ...(vendedor
      ? [
          { a: '/visitas', texto: 'Visitas', icono: 'route', fin: false },
          { a: '/', texto: 'Productos', icono: 'storefront', fin: true },
        ]
      : [{ a: '/', texto: 'Inicio', icono: 'storefront', fin: true }]),
    ...(vendedor ? [] : [{ a: '/buscar', texto: 'Buscar', icono: 'search', fin: false }]),
    // Sin cuenta, "Pedidos" lleva a entrar: ahí la que se marca es "Entrar", no las dos.
    {
      a: conSesion ? '/pedidos' : '/entrar',
      texto: 'Pedidos',
      icono: 'receipt_long',
      fin: false,
      prestado: !conSesion,
    },
    { a: '/carrito', texto: 'Carrito', icono: 'shopping_cart', fin: true },
    conSesion
      ? { a: '/perfil', texto: 'Cuenta', icono: 'person', fin: true }
      : { a: '/entrar', texto: 'Entrar', icono: 'login', fin: true },
  ];
  return (
    <nav
      aria-label="Secciones"
      className="seguro-abajo fixed inset-x-0 bottom-0 z-40 border-t border-stone-200 bg-white/95 shadow-[0_-2px_10px_rgb(0_0_0/0.07)] backdrop-blur md:hidden"
    >
      <ul className="mx-auto grid h-17 max-w-2xl grid-cols-5">
        {items.map((it) => (
          <li key={it.texto}>
            <NavLink
              to={it.a}
              end={it.fin}
              className={({ isActive }) =>
                cx(
                  'flex h-full flex-col items-center justify-center gap-0.5 text-[13px]',
                  'focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-primary-600',
                  isActive && !it.prestado
                    ? 'font-semibold text-primary-700'
                    : 'font-medium text-stone-600 hover:text-stone-900',
                )
              }
              aria-label={it.a === '/carrito' && articulos ? `Carrito, ${articulos} productos` : undefined}
            >
              {({ isActive }) => (
                <>
                  {/* Donde estás: una "píldora" del color de la marca detrás del ícono (Material 3). */}
                  <span
                    className={cx(
                      'relative grid h-8 w-16 place-items-center rounded-full transition-colors',
                      isActive && !it.prestado && 'bg-primary-50',
                    )}
                  >
                    <Icono nombre={it.icono} relleno={isActive && !it.prestado} tamano={26} />
                    {it.a === '/carrito' && articulos > 0 ? (
                      <Insignia n={articulos} className="-top-1.5 right-1.5" />
                    ) : null}
                  </span>
                  {it.texto}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * "Ver carrito · N · Gs. X": flota arriba de la barra de abajo. Desde tablet no está (taparía la grilla): el carrito, con
 * cuántos productos y el total, queda en la cabecera.
 */
export function BarraCarrito() {
  const carrito = useCarrito((s) => s.carrito);
  const navegar = useNavigate();
  const r = resumen(carrito);
  if (!r.articulos) return null;
  return (
    <div className="fixed inset-x-0 bottom-[calc(var(--alto-nav)+0.5rem)] z-30 px-3 md:hidden">
      <button
        type="button"
        onClick={() => navegar('/carrito')}
        className="animar-subir mx-auto flex min-h-16 w-full max-w-xl items-center gap-3 rounded-full bg-primary-600 py-2 pr-2.5 pl-5 text-left text-sobre-marca shadow-lg shadow-black/25 hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
      >
        <Icono nombre="shopping_cart" relleno tamano={26} />
        <span className="min-w-0 flex-1 leading-tight">
          <span className="block text-[15px] opacity-90">
            {plural(r.articulos, 'producto', 'productos')} · {plural(r.bultos, 'bulto', 'bultos')}
          </span>
          {r.monto !== null && r.monto > 0 ? (
            <span className="block text-lg font-bold tabular-nums">{formatoGs(r.monto)}</span>
          ) : null}
        </span>
        <span className="inline-flex min-h-12 shrink-0 items-center gap-0.5 rounded-full bg-white px-4 text-base font-bold text-primary-700">
          Ver carrito
          <Icono nombre="chevron_right" tamano={24} />
        </span>
      </button>
    </div>
  );
}
